import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
  setFuelTanks,
  setPowerPlantReserveHours,
  setJumpDriveReserveParsecs,
  setReactionDriveHours,
} from "../../../../store/features/shipBuilderSlice";
import { selectors } from "../../../../store/selectors";
import { formatCurrency } from "../../../../utils/formatting";
import { selectIsBreakaway } from "../../../../store/selectors/breakawayHullSelectors";
import { calculateJumpFuelConsumption } from "../../../../utils/calculations/fuelCalculations";
import { POWER_PLANTS } from "../../../../constants/powerPlants";
import { BreakawayFuelConfig } from "../core/BreakawayFuelConfig";

const FuelSelector: React.FC = () => {
  const dispatch = useAppDispatch();

  // Use Redux selectors for all fuel-related data
  const drives = useAppSelector((state) => state.shipBuilder.drives);
  const shipSize = useAppSelector((state) => state.shipBuilder.shipSize);
  const powerPlant = useAppSelector((state) => state.shipBuilder.powerPlant);

  const fuelTanks = useAppSelector((state) => state.shipBuilder.fuelTanks) || 0;
  const powerPlantReserveHours = useAppSelector(
    (state) => state.shipBuilder.powerPlantReserveHours,
  );
  const jumpDriveReserveParsecs = useAppSelector(
    (state) => state.shipBuilder.jumpDriveReserveParsecs,
  );
  const reactionDriveHours =
    useAppSelector((state) => state.shipBuilder.reactionDriveHours) || 1;

  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);

  // Check if breakaway hulls are configured
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const getBreakawayInfo = () => {
    if (!breakawayHull?.sections) return "";
    const sections = breakawayHull.sections;
    return `${sections.length} section${sections.length > 1 ? 's' : ''} (${sections.map(s => `${s.name}: ${s.size} tons`).join(", ")})`;
  };

  // Fuel requirement selectors
  const powerPlantFuel = useAppSelector(
    selectors.derived.selectPowerPlantFuelRequirement,
  );
  const reactionDriveFuel = useAppSelector(
    selectors.derived.selectReactionDriveFuelRequirement,
  );
  const jumpDriveFuel = useAppSelector(
    selectors.derived.selectJumpDriveFuelRequirement,
  );
  const totalRequired = useAppSelector(
    selectors.derived.selectTotalFuelRequirement,
  );


  // Fuel capacity selectors
  const optionalFuelCapacity = useAppSelector(
    selectors.derived.selectOptionalFuelSystemsCapacity,
  );
  const compartmentReduction = useAppSelector(
    selectors.derived.selectFuelCompartmentReduction,
  );
  const hasMetalHydride = useAppSelector(
    selectors.derived.selectHasMetalHydrideStorage,
  );

  const totalUsableFuel = useAppSelector(
    selectors.derived.selectTotalUsableFuel,
  ) as number;
  const maxAllocatable = useAppSelector(
    selectors.derived.selectMaxAllocatableFuel,
  );

  // Combat thrust selector
  const combatInfo = (useAppSelector(
    selectors.derived.selectCombatThrustInfo,
  ) || {}) as any;

  // Validation selector (kept for potential future use)
  // const fuelErrors = (useAppSelector(
  //   selectors.derived.selectFuelValidationErrors,
  // ) || []) as string[];

  // Fuel cost selectors for footer
  const fullRefuelCostRefined = useAppSelector(
    selectors.derived.selectFullRefuelCostRefined,
  ) as number;
  const fullRefuelCostUnrefined = useAppSelector(
    selectors.derived.selectFullRefuelCostUnrefined,
  ) as number;

  // Helper functions to calculate minimums
  const calculatePowerPlantMinWeeks = React.useCallback(() => {
    return 1; // Allow whole numbers only, minimum 1 week
  }, []);

  const calculateReactionDriveMinHours = React.useCallback(() => {
    return 1;
  }, []);

  // Drives
  const hasReactionDrive = drives.some((d) => d.type === "reaction");
  const jumpDrive = drives.find((d) => d.type === "jump");
  const maxJumpRating = jumpDrive?.rating;

  // --- Local state for all inputs ---
  const [powerPlantReserveWeeks, setPowerPlantReserveWeeksLocal] =
    React.useState<number>(
      Math.max(
        powerPlantReserveHours
          ? Math.ceil(powerPlantReserveHours / 168)
          : calculatePowerPlantMinWeeks(),
        1,
      ),
    );
  const [jumpDriveReserveParsecsLocal, setJumpDriveReserveParsecsLocal] =
    React.useState<number>(
      Math.max(jumpDriveReserveParsecs ?? 0, 0),
    );
  const [reactionDriveHoursLocal, setReactionDriveHoursLocal] =
    React.useState<number>(reactionDriveHours);

  // State for manual fuel tank allocation
  const [manualFuelTanks, setManualFuelTanks] = React.useState<number | null>(null);
  const [useManualFuelTanks, setUseManualFuelTanks] = React.useState(false);

  // Calculate auto fuel tanks based on requirements
  const autoFuelTanks = React.useMemo(() => {
    return Number(totalRequired);
  }, [totalRequired]);

  // Effective fuel tanks (manual or auto)
  const effectiveFuelTanks = useManualFuelTanks && manualFuelTanks !== null
    ? manualFuelTanks
    : autoFuelTanks;

  // Local validation using effective fuel tanks
  const localFuelErrors = React.useMemo(() => {
    const errors: string[] = [];
    if (effectiveFuelTanks < Number(totalRequired)) {
      errors.push(`Insufficient fuel tankage. Required: ${Number(totalRequired)} tons, Available: ${effectiveFuelTanks} tons`);
    }
    return errors;
  }, [effectiveFuelTanks, totalRequired]);

  // Use local validation instead of Redux validation for display
  const displayFuelErrors = localFuelErrors;
  const displayTotalUsableFuel = effectiveFuelTanks;

  // Sync local state with Redux/minimums on mount or when dependencies change
  React.useEffect(() => {
    setPowerPlantReserveWeeksLocal(
      Math.max(
        powerPlantReserveHours
          ? powerPlantReserveHours / 168 // Allow fractional weeks
          : calculatePowerPlantMinWeeks(),
        calculatePowerPlantMinWeeks(),
      ),
    );
  }, [powerPlantReserveHours, powerPlant?.type, calculatePowerPlantMinWeeks]);
  React.useEffect(() => {
    setJumpDriveReserveParsecsLocal(
      Math.max(jumpDriveReserveParsecs ?? 1, 0),
    );
  }, [jumpDriveReserveParsecs]);
  React.useEffect(() => {
    setReactionDriveHoursLocal(reactionDriveHours);
  }, [reactionDriveHours]);

  // Update manual fuel tanks when auto calculation changes
  React.useEffect(() => {
    if (!useManualFuelTanks) {
      setManualFuelTanks(autoFuelTanks);
    }
  }, [autoFuelTanks, useManualFuelTanks]);

  // Sync Redux state with effective fuel tanks
  React.useEffect(() => {
    if (!useManualFuelTanks) {
      dispatch(setFuelTanks(autoFuelTanks));
    }
  }, [autoFuelTanks, useManualFuelTanks, dispatch]);

  // Determine if the ship has a reaction drive installed
  // const hasReactionDrive = drives.some((d) => d.type === 'reaction');

  // Get installed jump drive and its rating
  // const jumpDrive = drives.find((d) => d.type === 'jump');
  // const maxJumpRating = jumpDrive?.rating || 1;

  // --- Ensure defaults for Power Plant Reserve ---
  // React.useEffect(() => {
  //   const minHours = calculatePowerPlantMinHours();
  //   if (typeof powerPlantReserveHours !== 'number' || powerPlantReserveHours < minHours) {
  //     dispatch(setPowerPlantReserveHours(minHours));
  //   }
  // }, [dispatch, powerPlantReserveHours]);

  // --- Ensure defaults for Jump Drive Reserve ---
  // React.useEffect(() => {
  //   const minParsecs = maxJumpRating;
  //   if (typeof jumpDriveReserveParsecs !== 'number' || jumpDriveReserveParsecs < minParsecs) {
  //     dispatch(setJumpDriveReserveParsecs(minParsecs));
  //   }
  // }, [dispatch, jumpDriveReserveParsecs, maxJumpRating]);

  // State for user-selected jump fuel reserve (parsecs)
  // const [jumpParsecs, setJumpParsecs] = React.useState<number>(maxJumpRating);

  // Update fuel tanks in store when auto-calculated requirements change
  useEffect(() => {
    // Only update Redux state if we're not using manual mode
    if (!useManualFuelTanks) {
      dispatch(setFuelTanks(autoFuelTanks));
    }
  }, [autoFuelTanks, useManualFuelTanks, dispatch]);

  // Handler: round up to nearest ton on change, and persist to Redux
  const handleCustomFuelTanksChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Math.ceil(Number(event.target.value));
    if (!isNaN(value) && value >= 0) {
      setManualFuelTanks(value);
      dispatch(setFuelTanks(value));
    }
  };

  // Handler for manual toggle
  const handleManualFuelTanksToggle = (useManual: boolean) => {
    setUseManualFuelTanks(useManual);
    if (!useManual) {
      setManualFuelTanks(null);
      dispatch(setFuelTanks(autoFuelTanks));
    }
  };

  // Handlers for Power Plant Reserve
  const handlePowerPlantReserveChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Math.round(Number(event.target.value)); // Round to whole number
    setPowerPlantReserveWeeksLocal(value);
    // Update Redux state immediately for real-time fuel calculation
    const min = calculatePowerPlantMinWeeks();
    const clamped = Math.max(value || min, min);
    dispatch(setPowerPlantReserveHours(clamped * 168)); // store as hours in Redux for compatibility
  };
  const handlePowerPlantReserveBlur = () => {
    const min = calculatePowerPlantMinWeeks();
    const rounded = Math.round(Number(powerPlantReserveWeeks) || min); // Round to whole number
    const clamped = Math.max(rounded, min);
    setPowerPlantReserveWeeksLocal(clamped);
    dispatch(setPowerPlantReserveHours(clamped * 168)); // store as hours in Redux for compatibility
  };

  // Handlers for Jump Drive Reserve
  const handleJumpDriveReserveChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Number(event.target.value);
    setJumpDriveReserveParsecsLocal(value);
    // Update Redux state immediately for real-time fuel calculation
    const min = 0; // Allow values down to 0
    const clamped = Math.max(value || 0, min);
    dispatch(setJumpDriveReserveParsecs(clamped));
  };
  const handleJumpDriveReserveBlur = () => {
    const min = 0; // Allow values down to 0
    const clamped = Math.max(Number(jumpDriveReserveParsecsLocal) || 0, min);
    setJumpDriveReserveParsecsLocal(clamped);
    dispatch(setJumpDriveReserveParsecs(clamped));
  };

  // Handlers for Reaction Drive Reserve
  const handleReactionDriveHoursChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Number(event.target.value);
    setReactionDriveHoursLocal(value);
    // Update Redux state immediately for real-time fuel calculation
    const min = calculateReactionDriveMinHours();
    const clamped = Math.max(value || min, min);
    dispatch(setReactionDriveHours(clamped));
  };
  const handleReactionDriveHoursBlur = () => {
    const min = calculateReactionDriveMinHours();
    const clamped = Math.max(Number(reactionDriveHoursLocal) || min, min);
    setReactionDriveHoursLocal(clamped);
    dispatch(setReactionDriveHours(clamped));
  };

  return (
    <div className="pb-20">
      {" "}
      {/* Add bottom padding to prevent content from being hidden behind sticky footer */}
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">
        Step 4: Install Fuel Tanks
      </h2>

      {/* Breakaway Hulls Detection */}
      {hasBreakawayHulls && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Breakaway Hulls Detected
            </h3>
            <p className="text-sm text-blue-800 mb-2">
              {getBreakawayInfo()}
            </p>
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> Each breakaway section can be assigned a percentage of total fuel reserves. 
              Each section must have enough fuel to power its systems when operating independently.
            </p>
          </div>
        </div>
      )}

      {/* Breakaway Fuel Configuration - Inline */}
      {hasBreakawayHulls && breakawayHull?.sections && breakawayHull.sections.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Breakaway Section Fuel Configuration</h3>
          <BreakawayFuelConfig
            isOpen={true}
            onClose={() => {}} // No-op since we're showing inline
            inline={true}
          />
        </div>
      )}

      {/* Standard Fuel Configuration - Hidden when breakaway hulls are enabled */}
      {!hasBreakawayHulls && (
        <>
          {/* All Fuel Reserves - Dynamic Horizontal Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Power Plant Reserve */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                Power Plant Reserve
              </h3>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Weeks of Power Plant Operation
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={powerPlantReserveWeeks}
                  min={calculatePowerPlantMinWeeks()}
                  step="1"
                  onChange={handlePowerPlantReserveChange}
                  onBlur={handlePowerPlantReserveBlur}
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {powerPlant && powerPlant.type ? (
                  `${POWER_PLANTS[powerPlant.type]?.name || powerPlant.type} (${powerPlant.weight} tons)`
                ) : (
                  "No power plant selected."
                )}
              </p>
              <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                Fuel Required:{" "}
                <span className="font-bold">{`${Number(powerPlantFuel)} tons`}</span>
              </div>
            </div>

            {/* Jump Drive Reserve Section */}
            {jumpDrive && maxJumpRating && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                  Jump Drive Reserve
                </h3>
                <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="jumpParsecs">
                  Number of Jumps Reserved for:
                </label>
                <input
                  id="jumpParsecs"
                  type="number"
                  min={0}
                  step={0.1}
                  value={jumpDriveReserveParsecsLocal}
                  onChange={handleJumpDriveReserveChange}
                  onBlur={handleJumpDriveReserveBlur}
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                />
                <p className="text-gray-700 dark:text-gray-300 mt-2 font-semibold">
                  Jump Fuel Required for 1 full jump:{" "}
                  <span className="font-bold">{`${calculateJumpFuelConsumption(maxJumpRating, shipSize).toFixed(2)} tons`}</span>
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-2 font-semibold">
                  Total Jump Fuel Allocated (enough for {jumpDriveReserveParsecsLocal} jumps):{" "}
                  <span className="font-bold">{`${(calculateJumpFuelConsumption(maxJumpRating, shipSize) * jumpDriveReserveParsecsLocal).toFixed(2)} tons`}</span>
                </p>
              </div>
            )}

            {/* Reaction Drive Reserve Section - only show if reaction drive is present */}
            {hasReactionDrive && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                  Reaction Drive Reserve
                </h3>
                <label
                  className="block text-gray-700 dark:text-gray-300 mb-2"
                  htmlFor="reactionDriveHours"
                >
                  Hours of Thruster Operation
                </label>
                <input
                  id="reactionDriveHours"
                  type="number"
                  min={1}
                  value={reactionDriveHoursLocal}
                  onChange={handleReactionDriveHoursChange}
                  onBlur={handleReactionDriveHoursBlur}
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                />
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
                  Minimum: 1 hour(s). 2.5% per thrust per hour.
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-2 font-semibold">
                  Fuel Required:{" "}
                  <span className="font-bold">{`${Number(reactionDriveFuel)} tons`}</span>
                </p>
              </div>
            )}
          </div>
      {/* Custom Fuel Tank Allocation */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Fuel Tank Allocation
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Fuel tanks are automatically calculated based on your fuel requirements, but you can manually adjust them for extended operations.
        </p>
        {/* Remove yellow info box here */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fuel Tank Allocation (tons)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={effectiveFuelTanks}
                onChange={handleCustomFuelTanksChange}
                min={0}
                max={Number(maxAllocatable)}
                step={1}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useManualFuelTanks}
                  onChange={(e) => handleManualFuelTanksToggle(e.target.checked)}
                  className="mr-2 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">Manual</span>
              </label>
            </div>
            {!useManualFuelTanks && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Auto-calculated based on fuel requirements
              </p>
            )}
            {/* Visual indicator for requirements */}
            <div className="mt-2">
              <span
                className={`flex items-center text-sm font-semibold ${displayFuelErrors.length > 0 ? "text-red-600" : displayTotalUsableFuel > Number(totalRequired) ? "text-green-700" : "text-yellow-600"}`}
              >
                {displayFuelErrors.length > 0 ? (
                  <>
                    <svg
                      className="w-5 h-5 mr-1 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Not Met
                  </>
                ) : totalUsableFuel > Number(totalRequired) ? (
                  <>
                    <svg
                      className="w-5 h-5 mr-1 text-green-700"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Exceeded
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5 mr-1 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01"
                      />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Met
                  </>
                )}
              </span>
            </div>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Max Allocatable:</span>
                <span className="text-gray-700 font-semibold">{`${Number(maxAllocatable)} tons (remaining ship tonnage)`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">
                  + Additional Fuel from Optional Systems:
                </span>
                <span className="text-blue-700 font-semibold">{`${Number(optionalFuelCapacity).toFixed(2)} tons`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">
                  - Fuel Storage Reduction from Optional Systems:
                </span>
                <span className="text-yellow-700 font-semibold">{`${Number(compartmentReduction).toFixed(2)} tons`}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                <span className="text-gray-800 font-bold">
                  = Total Fuel Tank Compartment Space
                  {hasMetalHydride ? " (Halved by Metal Hydride Storage)" : ""}:
                </span>
                <span className="text-green-700 font-bold">{`${Number(totalUsableFuel).toFixed(2)} tons`}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Combat Thrust Information (only if Reaction Drive is installed) */}
      {hasReactionDrive && combatInfo && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Combat Thrust Information
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            For combat calculations: Total Thrust = Thrust × Hours × 10 (10
            combat rounds per hour)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {typeof combatInfo.maxThrust === "number"
                  ? combatInfo.maxThrust.toLocaleString()
                  : "0"}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Max Thrust</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {typeof combatInfo.hours === "number"
                  ? combatInfo.hours.toLocaleString()
                  : "0"}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Hours</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {typeof combatInfo.roundsPerHour === "number"
                  ? combatInfo.roundsPerHour.toLocaleString()
                  : "0"}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Rounds/Hour</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                {typeof combatInfo.totalThrust === "number"
                  ? combatInfo.totalThrust.toLocaleString()
                  : "0"}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Total Thrust</div>
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* Breakaway Hulls Message - Show when breakaway hulls are enabled but no sections configured */}
      {hasBreakawayHulls && !breakawayHull?.sections?.length && (
        <div className="mb-6 p-4 border-2 border-yellow-300 rounded-lg bg-yellow-50">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
            Breakaway Hulls Detected
          </h3>
          <p className="text-sm text-yellow-700">
            Breakaway hulls are enabled but sections are not yet configured. 
            Please configure breakaway sections in the Hull step before configuring fuel.
          </p>
        </div>
      )}

      {/* Validation Warnings */}
      {displayFuelErrors.length > 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <h4 className="text-sm font-semibold text-red-800 mb-2">
            Insufficient fuel tankage!
          </h4>
          {displayFuelErrors.map((err, i) => (
            <p className="text-sm text-red-700" key={i}>
              {err}
            </p>
          ))}
        </div>
      )}
      {/* Sticky Footer */}
      {!hasBreakawayHulls && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
          <div className="container mx-auto">
            <div className="grid grid-cols-3 gap-4 text-center">
              {/* Total Fuel Tank Space */}
              <div>
                <div className="text-xs text-gray-300 mb-1">
                  TOTAL FUEL TANK SPACE
                </div>
                <div className="text-lg font-bold text-blue-400">{`${Number(displayTotalUsableFuel).toFixed(2)} tons`}</div>
              </div>
              {/* Full Refuel Cost */}
              <div>
                <div className="text-xs text-gray-300 mb-1">FULL REFUEL COST</div>
                <div className="flex flex-col items-center">
                  <span className="text-sm font-semibold text-green-400">
                    Refined: {formatCurrency(fullRefuelCostRefined)}
                  </span>
                  <span className="text-sm font-semibold text-orange-400">
                    Unrefined: {formatCurrency(fullRefuelCostUnrefined)}
                  </span>
                </div>
              </div>
              {/* Reaction Drive Thrust Total */}
              <div>
                <div className="text-xs text-gray-300 mb-1">
                  REACTION DRIVE THRUST TOTAL
                </div>
                <div className="text-lg font-bold text-yellow-400">
                  {combatInfo && typeof combatInfo.totalThrust === "number"
                    ? combatInfo.totalThrust.toLocaleString()
                    : "0"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breakaway Fuel Configuration Modal - Removed as we now use section-specific config */}
    </div>
  );
};

export default FuelSelector;
